import { useLanguage, t, formatCount } from "../../../i18n";
import React, { useEffect, useMemo, useState } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import Sidebar from "../../../components/layout/Sidebar/Sidebar";
import Header from "../../../components/layout/Header/Header";
import MobileOffcanvasNav from "../../../components/layout/MobileOffcanvasNav/MobileOffcanvasNav";
import VerificationNoticeBanner from "../../../components/common/VerificationNoticeBanner/VerificationNoticeBanner";
import EmptyState from "../../../components/common/EmptyState/EmptyState";
import ListingCard from "../../../components/listing/ListingCard/ListingCard";
import Footer from "../../../components/layout/Footer/Footer";
import { useAuth } from "../../../features/auth/context/AuthContext";
import getFavouriteRoomListings from "../../../features/shortlist/services/getFavouriteRoomListings";
import "./Shortlist.css";
import useDocumentTitle from "../../../hooks/useDocumentTitle";
function normalizeUser(sourceUser = {}) {
  return {
    id: sourceUser?.user_id || sourceUser?.id || "",
    user_id: sourceUser?.user_id || sourceUser?.id || "",
    name: sourceUser?.name || sourceUser?.full_name || "BaytBuddy User",
    full_name: sourceUser?.full_name || sourceUser?.name || "BaytBuddy User",
    email: sourceUser?.email || "",
    mobile: sourceUser?.mobile || "",
    profile_status: sourceUser?.profile_status || "",
    nafath_status: sourceUser?.nafath_status || ""
  };
}
function toTitleCase(value) {
  return String(value || "").replace(/_/g, " ").replace(/\s+/g, " ").trim().toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
}
function mapGenderPreference(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "male") return "Male Only";
  if (normalized === "female") return "Female Only";
  return value || "Not specified";
}
function mapFurnished(value) {
  if (typeof value === "boolean") {
    return value ? "Yes" : "No";
  }
  const normalized = String(value || "").trim().toLowerCase();
  if (["1", "true", "yes"].includes(normalized)) return "Yes";
  if (["0", "false", "no"].includes(normalized)) return "No";
  return value ? String(value) : "No";
}
function getPrimaryImage(listing = {}) {
  if (listing?.primary_image) {
    return listing.primary_image;
  }
  if (Array.isArray(listing?.images) && listing.images.length > 0) {
    const primaryImage = listing.images.find(item => item?.is_primary) || listing.images[0];
    return primaryImage?.url || "";
  }
  return "";
}
function mapFavouriteApiItemToCard(item) {
  const listing = item?.room_listing || {};
  return {
    id: listing?.id,
    title: listing?.title || "Untitled Listing",
    image: getPrimaryImage(listing),
    type: toTitleCase(listing?.room_type),
    gender: mapGenderPreference(listing?.gender_preference),
    district: listing?.neighbourhood?.name || listing?.district || listing?.address || "Location not specified",
    city: listing?.city?.name || listing?.city_name || "",
    nationality: Array.isArray(listing?.community_preferences_data) ? listing.community_preferences_data.map(community => community?.name).filter(Boolean) : [],
    price: Number(listing?.rent || 0),
    houseType: toTitleCase(listing?.house_type),
    furnished: mapFurnished(listing?.furnished),
    address: listing?.address || "",
    isShortlisted: Boolean(listing?.is_favourite),
    isOwner: Boolean(listing?.is_owner),
    favouriteId: item?.favourite_id || "",
    favouritedAt: item?.favourited_at || "",
    raw: listing
  };
}
export default function Shortlist() {
  useLanguage();
  useDocumentTitle("Shortlist");
  const [showMobileNav, setShowMobileNav] = useState(false);
  const [shortlistedRooms, setShortlistedRooms] = useState([]);
  const [isShortlistLoading, setIsShortlistLoading] = useState(true);
  const [shortlistError, setShortlistError] = useState("");
  const {
    user: authUser
  } = useAuth();
  const user = useMemo(() => normalizeUser(authUser || {}), [authUser]);
  useEffect(() => {
    let isMounted = true;
    const fetchFavouriteListings = async () => {
      try {
        setIsShortlistLoading(true);
        setShortlistError("");
        const response = await getFavouriteRoomListings();
        const favouriteItems = Array.isArray(response?.data?.data) ? response.data.data : [];
        const mappedListings = favouriteItems.map(mapFavouriteApiItemToCard).filter(item => item?.id);
        if (isMounted) {
          setShortlistedRooms(mappedListings);
        }
      } catch (error) {
        if (isMounted) {
          setShortlistedRooms([]);
          setShortlistError(error?.response?.data?.message || error?.message || "Failed to load shortlisted rooms.");
        }
      } finally {
        if (isMounted) {
          setIsShortlistLoading(false);
        }
      }
    };
    fetchFavouriteListings();
    return () => {
      isMounted = false;
    };
  }, []);
  const handleShortlistChange = (isNowShortlisted, listing) => {
    if (!isNowShortlisted) {
      setShortlistedRooms(prev => prev.filter(item => String(item?.id) !== String(listing?.id)));
      return;
    }
    setShortlistedRooms(prev => {
      const filtered = prev.filter(item => String(item?.id) !== String(listing?.id));
      return [{
        ...listing,
        isShortlisted: true
      }, ...filtered];
    });
  };
  return <div className="bb-dashboard-layout">
      <div className="bb-dashboard-layout__sidebar d-none d-lg-block">
        <Sidebar user={user} />
      </div>

      <div className="bb-dashboard-layout__content">
        <Header onMenuClick={() => setShowMobileNav(true)} />

        <VerificationNoticeBanner user={user} />

        <MobileOffcanvasNav show={showMobileNav} onHide={() => setShowMobileNav(false)} user={user} />


        <main className="bb-shortlist-main">
          <Container fluid>
            <div className="bb-shortlist-hero">
              <div>
                <h1 className="bb-shortlist-hero__title">{t("Shortlist")}</h1>
                <p className="bb-shortlist-hero__subtitle">{t("Review the rooms you shortlisted and remove the ones you no longer want to keep.")}


                </p>
              </div>
            </div>

            {isShortlistLoading ? <div className="bb-shortlist__loader">
                <Spinner animation="border" size="sm" />
                <span>{t("Loading shortlisted rooms...")}</span>
              </div> : shortlistError ? <EmptyState title={t("Unable to load shortlist")} description={t(shortlistError)} /> : shortlistedRooms.length ? <>
                <div className="bb-shortlist__results">
                  <p className="bb-shortlist__results-text">{t("You have {0}", {
                  0: formatCount(shortlistedRooms.length, "shortlisted room")
                })}
                  </p>
                </div>

                <Row className="g-4">
                  {shortlistedRooms.map(listing => <Col key={listing.id} xl={4} md={6}>
                      <ListingCard listing={listing} onShortlistChange={handleShortlistChange} />

                    </Col>)}
                </Row>
              </> : <EmptyState title={t("No shortlisted rooms yet")} description={t("Rooms you shortlist will appear here so you can revisit them later.")} />}
          </Container>
        </main>

        <Footer />
      </div>
    </div>;
}
